import { ChevronLeft, ChevronRight } from 'lucide-react'

export interface PagerProps {
  /** How many items the list holds in all. */
  total: number
  pageSize: number
  /** The page shown, from 0. */
  page: number
  onPage: (page: number) => void
  /** What the items are, for the screen reader: "open shifts". */
  label: string
}

/** The page a list should show once its length changes: never past the last page. */
export function clampPage(page: number, total: number, pageSize: number): number {
  return Math.max(0, Math.min(page, Math.ceil(total / pageSize) - 1))
}

/**
 * A pager for a list shown a page at a time (the user's call, 27 October 2026: a page, never a list that grows under
 * the reader): the items on this page of the total, previous and next, and a dot per page while there are few. Renders
 * nothing while everything fits on one page. The icons are pressed on their own, in the Month stepper's style.
 */
export function Pager({ total, pageSize, page, onPage, label }: PagerProps) {
  const pages = Math.ceil(total / pageSize)
  if (pages <= 1) {
    return null
  }
  const current = clampPage(page, total, pageSize)
  const first = current * pageSize + 1
  const last = Math.min(total, (current + 1) * pageSize)
  const step = 'rounded p-1 text-marine hover:bg-marine/10 disabled:opacity-30'
  return (
    <nav className="flex flex-wrap items-center gap-2 text-[11px] text-ink-mid" aria-label={`Pages of ${label}`}>
      <button type="button" aria-label="Previous page" className={step} disabled={current === 0} onClick={() => onPage(current - 1)}>
        <ChevronLeft className="h-4 w-4" aria-hidden="true" />
      </button>
      <span className="font-bold text-ink">
        {first}–{last} <span className="font-normal text-ink-mid">of {total}</span>
      </span>
      {pages <= 8 && (
        <span className="flex items-center gap-1" aria-hidden="true">
          {Array.from({ length: pages }, (_, index) => (
            <button key={index} type="button" tabIndex={-1} className={`h-2 w-2 rounded-full ${index === current ? 'bg-marine' : 'bg-marine/25 hover:bg-marine/50'}`} onClick={() => onPage(index)} />
          ))}
        </span>
      )}
      <button type="button" aria-label="Next page" className={step} disabled={current >= pages - 1} onClick={() => onPage(current + 1)}>
        <ChevronRight className="h-4 w-4" aria-hidden="true" />
      </button>
      <span className="sr-only">
        Page {current + 1} of {pages}
      </span>
    </nav>
  )
}
